// The food table. Static content, not database rows: it is the same for
// everyone and changes only when we edit it.
//
// WHAT THIS IS: a general guide written from widely published low-FODMAP
// guidance. Ratings assume a typical single serving. FODMAP content depends
// on portion, ripeness and preparation, and published thresholds are revised
// as foods are re-tested — so "low" means "usually fine at a normal portion",
// not "unlimited". It is not the Monash University dataset and must not be
// presented as such, and it is not medical advice (the app says so on screen).
//
// Adding a food: one row. `groups` lists the FODMAPs that make it moderate or
// high (empty for a low food with nothing to watch); `note` picks one of the
// translated notes in i18n (`foods.notes.*`).

export type FodmapGroup = 'fructans' | 'gos' | 'lactose' | 'fructose' | 'sorbitol' | 'mannitol'
export const FODMAP_GROUPS: readonly FodmapGroup[] = ['fructans', 'gos', 'lactose', 'fructose', 'sorbitol', 'mannitol']

export type Level = 'low' | 'moderate' | 'high'
export const LEVELS: readonly Level[] = ['low', 'moderate', 'high']

export type Category =
  | 'vegetables'
  | 'fruit'
  | 'grains'
  | 'protein'
  | 'dairy'
  | 'legumes'
  | 'nuts'
  | 'sweets'
  | 'drinks'
  | 'pantry'
export const CATEGORIES: readonly Category[] = [
  'vegetables',
  'fruit',
  'grains',
  'protein',
  'dairy',
  'legumes',
  'nuts',
  'sweets',
  'drinks',
  'pantry',
]

export type FoodNote =
  | 'smallOk'
  | 'largeAdds'
  | 'ripeness'
  | 'greenOnly'
  | 'cannedRinsed'
  | 'checkLabel'
  | 'firmOnly'
  | 'infused'
  | 'alcohol'
  | 'soyProtein'
  | 'weakBrew'

export type Food = {
  id: string
  en: string
  pt: string
  category: Category
  level: Level
  groups: readonly FodmapGroup[]
  note?: FoodNote
}

type Row = [id: string, en: string, pt: string, level: Level, groups?: FodmapGroup[], note?: FoodNote]

function rows(category: Category, list: Row[]): Food[] {
  return list.map(([id, en, pt, level, groups = [], note]) => ({ id, en, pt, category, level, groups, note }))
}

export const FOODS: readonly Food[] = [
  ...rows('vegetables', [
    ['carrot', 'Carrot', 'Cenoura', 'low'],
    ['cucumber', 'Cucumber', 'Pepino', 'low'],
    ['lettuce', 'Lettuce', 'Alface', 'low'],
    ['spinach', 'Spinach', 'Espinafres', 'low'],
    ['potato', 'Potato', 'Batata', 'low'],
    ['tomato', 'Tomato', 'Tomate', 'low', ['fructose'], 'largeAdds'],
    ['zucchini', 'Zucchini (courgette)', 'Curgete', 'low', ['fructans'], 'largeAdds'],
    ['eggplant', 'Eggplant (aubergine)', 'Beringela', 'low'],
    ['green-pepper', 'Green bell pepper', 'Pimento verde', 'low', ['fructans'], 'largeAdds'],
    ['green-beans', 'Green beans', 'Feijão-verde', 'low', ['sorbitol'], 'largeAdds'],
    ['bok-choy', 'Bok choy', 'Couve-chinesa (pak choi)', 'low'],
    ['kale', 'Kale', 'Couve kale', 'low'],
    ['pumpkin', 'Pumpkin (Japanese / kabocha)', 'Abóbora (hokkaido)', 'low'],
    ['parsnip', 'Parsnip', 'Pastinaca', 'low'],
    ['radish', 'Radish', 'Rabanete', 'low'],
    ['olives', 'Olives', 'Azeitonas', 'low'],
    ['ginger', 'Ginger', 'Gengibre', 'low'],
    ['chives', 'Chives', 'Cebolinho', 'low'],
    ['spring-onion-green', 'Spring onion (green tops)', 'Cebola nova (parte verde)', 'low', [], 'greenOnly'],
    ['leek-leaves', 'Leek (green leaves)', 'Alho-francês (folhas verdes)', 'low', [], 'greenOnly'],
    ['cabbage', 'Cabbage (common green)', 'Couve-coração / repolho', 'low', ['sorbitol'], 'largeAdds'],
    ['broccoli', 'Broccoli', 'Brócolos', 'moderate', ['fructose'], 'smallOk'],
    ['brussels-sprouts', 'Brussels sprouts', 'Couves-de-bruxelas', 'moderate', ['fructans'], 'smallOk'],
    ['beetroot', 'Beetroot', 'Beterraba', 'moderate', ['fructans', 'gos'], 'smallOk'],
    ['sweet-potato', 'Sweet potato', 'Batata-doce', 'moderate', ['mannitol'], 'smallOk'],
    ['sweetcorn', 'Sweetcorn', 'Milho-doce', 'moderate', ['sorbitol', 'gos'], 'smallOk'],
    ['celery', 'Celery', 'Aipo', 'moderate', ['mannitol'], 'smallOk'],
    ['butternut', 'Butternut squash', 'Abóbora-manteiga', 'moderate', ['gos', 'mannitol'], 'smallOk'],
    ['savoy-cabbage', 'Savoy cabbage', 'Couve-lombarda', 'moderate', ['fructans'], 'smallOk'],
    ['onion', 'Onion', 'Cebola', 'high', ['fructans']],
    ['garlic', 'Garlic', 'Alho', 'high', ['fructans']],
    ['leek-bulb', 'Leek (white bulb)', 'Alho-francês (parte branca)', 'high', ['fructans']],
    ['shallot', 'Shallot', 'Chalota', 'high', ['fructans']],
    ['cauliflower', 'Cauliflower', 'Couve-flor', 'high', ['mannitol']],
    ['mushrooms', 'Mushrooms (button)', 'Cogumelos (brancos)', 'high', ['mannitol']],
    ['asparagus', 'Asparagus', 'Espargos', 'high', ['fructose', 'fructans']],
    ['artichoke', 'Artichoke', 'Alcachofra', 'high', ['fructans']],
    ['peas', 'Green peas', 'Ervilhas', 'high', ['gos', 'fructans']],
    ['snow-peas', 'Snow peas (mangetout)', 'Ervilhas-tortas', 'high', ['fructans', 'mannitol']],
  ]),
  ...rows('fruit', [
    ['strawberry', 'Strawberries', 'Morangos', 'low'],
    ['blueberry', 'Blueberries', 'Mirtilos', 'low', ['fructans'], 'largeAdds'],
    ['raspberry', 'Raspberries', 'Framboesas', 'low', ['fructans'], 'largeAdds'],
    ['orange', 'Orange', 'Laranja', 'low'],
    ['mandarin', 'Mandarin / clementine', 'Tangerina / clementina', 'low'],
    ['kiwi', 'Kiwi', 'Kiwi', 'low'],
    ['pineapple', 'Pineapple', 'Ananás', 'low', ['fructans'], 'largeAdds'],
    ['papaya', 'Papaya', 'Papaia', 'low'],
    ['cantaloupe', 'Cantaloupe melon', 'Meloa', 'low', ['fructans'], 'largeAdds'],
    ['lemon', 'Lemon and lime', 'Limão e lima', 'low'],
    ['passion-fruit', 'Passion fruit', 'Maracujá', 'low'],
    ['banana', 'Banana', 'Banana', 'moderate', ['fructans'], 'ripeness'],
    ['grapes', 'Grapes', 'Uvas', 'moderate', ['fructose'], 'smallOk'],
    ['avocado', 'Avocado', 'Abacate', 'moderate', ['sorbitol'], 'smallOk'],
    ['pomegranate', 'Pomegranate', 'Romã', 'moderate', ['fructans'], 'smallOk'],
    ['apple', 'Apple', 'Maçã', 'high', ['fructose', 'sorbitol']],
    ['pear', 'Pear', 'Pera', 'high', ['fructose', 'sorbitol']],
    ['mango', 'Mango', 'Manga', 'high', ['fructose']],
    ['watermelon', 'Watermelon', 'Melancia', 'high', ['fructose', 'fructans', 'mannitol']],
    ['cherries', 'Cherries', 'Cerejas', 'high', ['fructose', 'sorbitol']],
    ['peach', 'Peach', 'Pêssego', 'high', ['sorbitol', 'fructans']],
    ['nectarine', 'Nectarine', 'Nectarina', 'high', ['sorbitol', 'fructans']],
    ['plum', 'Plum', 'Ameixa', 'high', ['sorbitol', 'fructans']],
    ['apricot', 'Apricot', 'Alperce', 'high', ['sorbitol', 'fructans']],
    ['blackberry', 'Blackberries', 'Amoras', 'high', ['sorbitol']],
    ['fig', 'Figs', 'Figos', 'high', ['fructose']],
    ['dried-fruit', 'Dried fruit (raisins, dates, prunes)', 'Fruta seca (passas, tâmaras, ameixas)', 'high', ['fructans', 'fructose', 'sorbitol']],
  ]),
  ...rows('grains', [
    ['rice', 'Rice', 'Arroz', 'low'],
    ['oats', 'Oats', 'Aveia', 'low', ['fructans', 'gos'], 'largeAdds'],
    ['quinoa', 'Quinoa', 'Quinoa', 'low'],
    ['polenta', 'Polenta / cornmeal', 'Polenta / farinha de milho', 'low'],
    ['buckwheat', 'Buckwheat', 'Trigo-sarraceno', 'low'],
    ['millet', 'Millet', 'Milho-painço', 'low'],
    ['rice-noodles', 'Rice noodles', 'Massa de arroz', 'low'],
    ['gf-pasta', 'Gluten-free pasta', 'Massa sem glúten', 'low', [], 'checkLabel'],
    ['gf-bread', 'Gluten-free bread', 'Pão sem glúten', 'low', [], 'checkLabel'],
    ['corn-tortilla', 'Corn tortilla', 'Tortilha de milho', 'low', [], 'checkLabel'],
    ['sourdough-spelt', 'Sourdough spelt bread', 'Pão de espelta de fermentação lenta', 'low', ['fructans'], 'largeAdds'],
    ['popcorn', 'Popcorn (plain)', 'Pipocas (simples)', 'low', [], 'largeAdds'],
    ['rice-cakes', 'Rice cakes', 'Tortas de arroz', 'low', ['fructans'], 'largeAdds'],
    ['wheat-bread', 'Wheat bread', 'Pão de trigo', 'high', ['fructans']],
    ['wheat-pasta', 'Wheat pasta', 'Massa de trigo', 'high', ['fructans']],
    ['rye-bread', 'Rye bread', 'Pão de centeio', 'high', ['fructans']],
    ['barley', 'Barley', 'Cevada', 'high', ['fructans']],
    ['couscous', 'Couscous (wheat)', 'Cuscuz (trigo)', 'high', ['fructans']],
    ['wheat-cereal', 'Wheat breakfast cereal, crackers, biscuits', 'Cereais, bolachas e tostas de trigo', 'high', ['fructans']],
  ]),
  ...rows('protein', [
    ['chicken', 'Chicken and turkey (plain)', 'Frango e peru (simples)', 'low'],
    ['beef', 'Beef, pork, lamb (plain)', 'Vaca, porco, borrego (simples)', 'low'],
    ['fish', 'Fish and seafood (plain)', 'Peixe e marisco (simples)', 'low'],
    ['eggs', 'Eggs', 'Ovos', 'low'],
    ['firm-tofu', 'Firm tofu', 'Tofu firme', 'low', [], 'firmOnly'],
    ['tempeh', 'Tempeh (plain)', 'Tempeh (simples)', 'low'],
    ['canned-tuna', 'Canned tuna', 'Atum em lata', 'low'],
    ['sausages', 'Sausages, burgers, marinated meat', 'Salsichas, hambúrgueres, carne marinada', 'moderate', ['fructans'], 'checkLabel'],
    ['silken-tofu', 'Silken tofu', 'Tofu sedoso', 'high', ['gos']],
  ]),
  ...rows('dairy', [
    ['lf-milk', 'Lactose-free milk', 'Leite sem lactose', 'low'],
    ['lf-yogurt', 'Lactose-free yogurt', 'Iogurte sem lactose', 'low', [], 'checkLabel'],
    ['hard-cheese', 'Hard cheese (cheddar, parmesan, Swiss)', 'Queijo curado (cheddar, parmesão, flamengo)', 'low'],
    ['mozzarella', 'Mozzarella', 'Mozzarella', 'low', ['lactose'], 'largeAdds'],
    ['feta', 'Feta', 'Feta', 'low', ['lactose'], 'largeAdds'],
    ['brie', 'Brie and camembert', 'Brie e camembert', 'low'],
    ['butter', 'Butter', 'Manteiga', 'low'],
    ['almond-milk', 'Almond milk', 'Bebida de amêndoa', 'low'],
    ['rice-milk', 'Rice milk', 'Bebida de arroz', 'low'],
    ['soy-milk-protein', 'Soy milk (made from soy protein)', 'Bebida de soja (de proteína de soja)', 'low', [], 'soyProtein'],
    ['coconut-milk', 'Coconut milk (canned)', 'Leite de coco (lata)', 'low', ['sorbitol'], 'largeAdds'],
    ['soft-cheese', 'Ricotta, cottage and cream cheese', 'Ricotta, requeijão e queijo-creme', 'moderate', ['lactose'], 'smallOk'],
    ['cream', 'Cream', 'Natas', 'moderate', ['lactose'], 'smallOk'],
    ['oat-milk', 'Oat milk', 'Bebida de aveia', 'moderate', ['fructans', 'gos'], 'smallOk'],
    ['milk', "Milk (cow's, goat's, sheep's)", 'Leite (vaca, cabra, ovelha)', 'high', ['lactose']],
    ['yogurt', 'Yogurt', 'Iogurte', 'high', ['lactose']],
    ['ice-cream', 'Ice cream', 'Gelado', 'high', ['lactose']],
    ['custard', 'Custard and milk puddings', 'Pudins e sobremesas lácteas', 'high', ['lactose']],
    ['soy-milk-bean', 'Soy milk (made from whole soybeans)', 'Bebida de soja (de grão inteiro)', 'high', ['gos'], 'soyProtein'],
  ]),
  ...rows('legumes', [
    ['edamame', 'Edamame', 'Edamame', 'low', ['fructans'], 'largeAdds'],
    ['canned-lentils', 'Canned lentils', 'Lentilhas de lata', 'moderate', ['gos'], 'cannedRinsed'],
    ['canned-chickpeas', 'Canned chickpeas', 'Grão-de-bico de lata', 'moderate', ['gos'], 'cannedRinsed'],
    ['hummus', 'Hummus', 'Húmus', 'moderate', ['gos', 'fructans'], 'checkLabel'],
    ['dried-beans', 'Beans (kidney, black, white, baked)', 'Feijão (encarnado, preto, branco, cozido)', 'high', ['gos', 'fructans']],
    ['dried-lentils', 'Lentils cooked from dry', 'Lentilhas secas cozidas', 'high', ['gos']],
    ['broad-beans', 'Broad beans', 'Favas', 'high', ['gos']],
    ['split-peas', 'Split peas', 'Ervilhas secas', 'high', ['gos']],
  ]),
  ...rows('nuts', [
    ['peanuts', 'Peanuts', 'Amendoins', 'low'],
    ['peanut-butter', 'Peanut butter', 'Manteiga de amendoim', 'low', [], 'checkLabel'],
    ['walnuts', 'Walnuts', 'Nozes', 'low'],
    ['macadamia', 'Macadamia nuts', 'Macadâmias', 'low'],
    ['pecans', 'Pecans', 'Nozes-pecã', 'low'],
    ['brazil-nuts', 'Brazil nuts', 'Castanhas-do-pará', 'low'],
    ['pine-nuts', 'Pine nuts', 'Pinhões', 'low'],
    ['chia', 'Chia seeds', 'Sementes de chia', 'low'],
    ['pumpkin-seeds', 'Pumpkin seeds', 'Sementes de abóbora', 'low'],
    ['sunflower-seeds', 'Sunflower seeds', 'Sementes de girassol', 'low'],
    ['sesame', 'Sesame seeds', 'Sementes de sésamo', 'low'],
    ['almonds', 'Almonds', 'Amêndoas', 'moderate', ['gos'], 'smallOk'],
    ['hazelnuts', 'Hazelnuts', 'Avelãs', 'moderate', ['gos'], 'smallOk'],
    ['cashews', 'Cashews', 'Cajus', 'high', ['gos', 'fructans']],
    ['pistachios', 'Pistachios', 'Pistácios', 'high', ['gos', 'fructans']],
  ]),
  ...rows('sweets', [
    ['sugar', 'Table sugar', 'Açúcar', 'low'],
    ['maple-syrup', 'Maple syrup', 'Xarope de ácer', 'low'],
    ['rice-malt', 'Rice malt syrup', 'Xarope de arroz', 'low'],
    ['dark-chocolate', 'Dark chocolate', 'Chocolate negro', 'low', ['lactose'], 'largeAdds'],
    ['strawberry-jam', 'Strawberry jam', 'Doce de morango', 'low', [], 'checkLabel'],
    ['milk-chocolate', 'Milk and white chocolate', 'Chocolate de leite e branco', 'moderate', ['lactose'], 'smallOk'],
    ['honey', 'Honey', 'Mel', 'high', ['fructose']],
    ['agave', 'Agave syrup', 'Xarope de agave', 'high', ['fructose']],
    ['hfcs', 'High-fructose corn syrup', 'Xarope de milho rico em frutose', 'high', ['fructose']],
    ['polyols', 'Sugar-free gum and sweets (sorbitol, mannitol, xylitol)', 'Pastilhas e doces sem açúcar (sorbitol, manitol, xilitol)', 'high', ['sorbitol', 'mannitol']],
  ]),
  ...rows('drinks', [
    ['water', 'Water', 'Água', 'low'],
    ['coffee', 'Coffee (black, or with a low-FODMAP milk)', 'Café (simples, ou com leite baixo em FODMAP)', 'low'],
    ['black-tea', 'Black tea', 'Chá preto', 'low', ['fructans'], 'weakBrew'],
    ['green-tea', 'Green tea', 'Chá verde', 'low'],
    ['peppermint-tea', 'Peppermint tea', 'Chá de hortelã-pimenta', 'low'],
    ['wine', 'Wine (one glass)', 'Vinho (um copo)', 'low', [], 'alcohol'],
    ['beer', 'Beer (one)', 'Cerveja (uma)', 'low', [], 'alcohol'],
    ['orange-juice', 'Orange juice', 'Sumo de laranja', 'moderate', ['fructose'], 'smallOk'],
    ['coconut-water', 'Coconut water', 'Água de coco', 'moderate', ['sorbitol', 'fructans'], 'smallOk'],
    ['kombucha', 'Kombucha', 'Kombucha', 'moderate', ['fructans'], 'smallOk'],
    ['apple-juice', 'Apple and pear juice', 'Sumo de maçã e de pera', 'high', ['fructose', 'sorbitol']],
    ['chamomile-tea', 'Chamomile tea', 'Chá de camomila', 'high', ['fructans']],
    ['fennel-tea', 'Fennel tea', 'Chá de funcho', 'high', ['fructans']],
    ['rum', 'Rum', 'Rum', 'high', ['fructose'], 'alcohol'],
  ]),
  ...rows('pantry', [
    ['olive-oil', 'Olive oil and other oils', 'Azeite e outros óleos', 'low'],
    ['garlic-oil', 'Garlic-infused oil', 'Azeite aromatizado com alho', 'low', [], 'infused'],
    ['herbs', 'Fresh herbs (basil, parsley, coriander, mint)', 'Ervas frescas (manjericão, salsa, coentros, hortelã)', 'low'],
    ['spices', 'Pure spices (cumin, paprika, turmeric, pepper)', 'Especiarias puras (cominhos, pimentão, açafrão, pimenta)', 'low'],
    ['soy-sauce', 'Soy sauce', 'Molho de soja', 'low'],
    ['mustard', 'Mustard', 'Mostarda', 'low'],
    ['mayonnaise', 'Mayonnaise', 'Maionese', 'low', [], 'checkLabel'],
    ['vinegar', 'Vinegar (wine, rice, cider in small amounts)', 'Vinagre (vinho, arroz, sidra em pouca quantidade)', 'low'],
    ['tomato-paste', 'Tomato paste', 'Concentrado de tomate', 'low', ['fructose'], 'largeAdds'],
    ['ketchup', 'Ketchup', 'Ketchup', 'moderate', ['fructans'], 'smallOk'],
    ['balsamic', 'Balsamic vinegar', 'Vinagre balsâmico', 'moderate', ['fructose'], 'smallOk'],
    ['stock', 'Stock cubes, gravy, ready-made sauces', 'Caldos, molhos prontos e temperos preparados', 'high', ['fructans'], 'checkLabel'],
    ['garlic-powder', 'Garlic and onion powder', 'Alho e cebola em pó', 'high', ['fructans']],
    ['inulin', 'Inulin / chicory root fibre (added to "high-fibre" foods)', 'Inulina / fibra de chicória (em alimentos "ricos em fibra")', 'high', ['fructans'], 'checkLabel'],
  ]),
]

/** Lower-case and strip accents, so "maca" finds "Maçã". */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

export type FoodFilter = {
  query: string
  level: Level | null
  category: Category | null
  group: FodmapGroup | null
}

/** Filter the table. A search matches the name in either language. */
export function filterFoods(foods: readonly Food[], filter: FoodFilter): Food[] {
  const q = normalize(filter.query.trim())
  return foods.filter(
    (food) =>
      (!filter.level || food.level === filter.level) &&
      (!filter.category || food.category === filter.category) &&
      (!filter.group || food.groups.includes(filter.group)) &&
      (!q || normalize(food.en).includes(q) || normalize(food.pt).includes(q)),
  )
}
